import React, {ChangeEvent, FormEvent, useEffect, useState} from 'react';
import Layout from "../components/Layout";
import Header from "../components/Header";
import {Button} from "react-bootstrap";
import {useManageContext} from "../App";
import {useParams, useSearchParams} from "react-router-dom";
import {TaskType} from "../types/Task";
import {formatDateForTask, getTaskFromTrip} from "../Helper";
import TaskActionField from "../components/TaskActionField";
import {FeedbackType, ToastType, ValidationFeedback} from "../types/General";
import {mdiCheck, mdiMessageAlertOutline, mdiShippingPallet, mdiThermometer} from "@mdi/js";
import i18n, {LanguagesIds} from "../lang/i18n";
import general from "../constants/General";
import Icon from "@mdi/react";
import FeedbackField from "../components/FeedbackField";
import Datetime from 'react-datetime';
import {Moment} from "moment/moment";
import GlobalSpinner from "../components/GlobalSpinner";
import axios from "axios";

type validationMsgType = {
    TaskCompletedDate: ValidationFeedback,
    PllCount: ValidationFeedback,
    PllStackable: ValidationFeedback,
    Temperature: ValidationFeedback,
    PllExchange: ValidationFeedback,
    Bruto: ValidationFeedback,
    Ldm: ValidationFeedback,
    Volume: ValidationFeedback,
    Notes: ValidationFeedback,
}

type ConfirmTaskForm = {
    TaskCompletedDate: Date,
    PllCount: number | null,
    PllStackable: number | null,
    PllExchange: number | null,
    Temperature: number | null,
    Ldm: number | null,
    Bruto: number | null,
    Volume: number | null,
    Notes: string | null,
}

export default function ConfirmDelivery() {

    const {tripKey} = useParams();
    const {trip, lang, setToast} = useManageContext();
    const {taskId} = useParams();
    const [task, setTask] = useState<TaskType | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const validationMsgInitialState: validationMsgType = {
        TaskCompletedDate: {type: null, text: ''},
        PllCount: {type: null, text: ''},
        PllStackable: {type: null, text: ''},
        Temperature: {type: null, text: ''},
        PllExchange: {type: null, text: ''},
        Bruto: {type: null, text: ''},
        Ldm: {type: null, text: ''},
        Volume: {type: null, text: ''},
        Notes: {type: null, text: ''},
    };
    const [formData, setFormData] = useState<ConfirmTaskForm>({
        TaskCompletedDate: new Date(),
        PllCount: null,
        PllStackable: null,
        PllExchange: null,
        Temperature: null,
        Ldm: null,
        Bruto: null,
        Volume: null,
        Notes: null,
    });

    const [validationMsg, setValidationMsg] = useState<validationMsgType>(validationMsgInitialState);
    const [searchParams] = useSearchParams();

    const TourId = searchParams.get('TourId');

    useEffect(() => {
        if (!task) {
            return;
        }

        setFormData((prevState): ConfirmTaskForm => ({
            TaskCompletedDate: prevState.TaskCompletedDate,
            PllCount: task.TaskType === 2 ? task.PllCount : null,
            PllStackable: task.TaskType === 2 ? task.PllStackable : null,
            PllExchange: task.TaskType === 2 ? task.PllExchange : null,
            Temperature: task.TaskType === 2 ? task.Temperature : null,
            Ldm: task.TaskType === 2 ? task.Ldm : null,
            Bruto: task.TaskType === 2 ? task.Bruto : null,
            Volume: task.TaskType === 2 ? task.Volume : null,
            Notes: null,
        }));
    }, [task]);

    useEffect(() => {
        if (trip && taskId) {
            for (let i = 0; i <= (trip?.Address.length ?? 0); i++) {
                const Task = getTaskFromTrip(trip, taskId);

                if (Task) {
                    setTask(Task);
                    break;
                }
            }
        }
    }, [trip, taskId]);

    const editField = (e: ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value,
        }));
        validateFields(name, value);
    };

    const editDateField = (value: Moment | string) => {
        setFormData(prevState => ({
            ...prevState,
            TaskCompletedDate: typeof value === 'string' ? new Date(value) : value.toDate(),
        }));
    }

    const editSelectField = (event: ChangeEvent<HTMLSelectElement>) => {
        const {name, value} = event.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        validateFields(name, value);
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (!validateFields()) {
            return;
        }

        setIsLoading(true);

        try {
            const {data} = await axios.post(`${general.apiUrl}/set-driver-tasks-all`, [{
                TripKey: tripKey,
                TaskKey: task?.TaskGuid,
                TaskType: task?.TaskType,
                TaskId: task?.TaskId,
                IpAddress: '0',
                Latitude: 0,
                Longitude: 0,
                LanguageId: LanguagesIds[lang],
                AppIdFrom: 0,
                FormId: 'browser',
                UserName: 'browser',
                ...formData,
                TaskCompletedDate: formatDateForTask(formData.TaskCompletedDate),
            }]);

            if (data.Status === 1) {
                setToast({
                    type: ToastType.Success,
                    title: 'Done!',
                    text: i18n.t('2682'),
                    iconPath: mdiCheck,
                });
                window.location.href = '/' + tripKey + (TourId ? '?TourId=' + TourId : '');

                return;
            }

            console.warn(data);
            window.alert(i18n.t('2589'));
        } catch (e) {
            console.warn(e);
            window.alert(i18n.t('2589'));
        }

        setIsLoading(false);
    };

    const validateField = (
        fieldKey: string,
        value: any = null,
        plannedValue: number | null | { from: number, to: number },
        isMandatory: boolean,
        isVisible: boolean,
    ) => {
        if (isMandatory && value === null) {
            setValidationMsg(prevState => ({
                ...prevState,
                [fieldKey]: {
                    type: FeedbackType.Error,
                    text: 'Field required.',
                },
            }));

            return false;
        }

        const diffThanPlanned = plannedValue && typeof plannedValue === 'object'
            ? Number(value) < Number(plannedValue.from) || Number(value) > Number(plannedValue.to)
            : Number(value) !== Number(plannedValue);

        if (isVisible && value && plannedValue && diffThanPlanned) {
            setValidationMsg(prevState => ({
                ...prevState,
                [fieldKey]: {
                    type: FeedbackType.Warning,
                    text: 'Different from panned.',
                },
            }));

            return true;
        }

        setValidationMsg(prevState => ({
            ...prevState,
            [fieldKey]: {
                type: null,
                text: '',
            },
        }));

        return true;
    }

    const validateFields = (fieldKey: string | null = null, value: any = null) => {
        let isValid = true;

        if (!fieldKey || fieldKey === 'PllCount') {
            if (!validateField(
                'PllCount',
                value ?? formData.PllCount,
                task?.PllCount ?? null,
                task?.PllCountIsMandatory ?? false,
                task?.PllCountIsVisible ?? false,
            )) {
                isValid = false;
            }
        }

        if (!fieldKey || fieldKey === 'PllStackable') {
            if (!validateField(
                'PllStackable',
                value ?? formData.PllStackable,
                task?.PllStackable ?? null,
                task?.PllStackableIsMandatory ?? false,
                task?.PllStackableIsVisible ?? false,
            )) {
                isValid = false;
            }
        }

        if (!fieldKey || fieldKey === 'Temperature') {
            if (!validateField(
                'Temperature',
                value ?? formData.Temperature,
                {from: task?.TemperatureFrom ?? 0, to: task?.TemperatureTo ?? 0},
                task?.TemperatureIsMandatory ?? false,
                task?.TemperatureIsVisible ?? false,
            )) {
                isValid = false;
            }
        }

        if (!fieldKey || fieldKey === 'Bruto') {
            if (!validateField(
                'Bruto',
                value ?? formData.Bruto,
                task?.Bruto ?? null,
                task?.BrutoIsMandatory ?? false,
                task?.BrutoIsVisible ?? false,
            )) {
                isValid = false;
            }
        }

        if (!fieldKey || fieldKey === 'Ldm') {
            if (!validateField(
                'Ldm',
                value ?? formData.Ldm,
                task?.Ldm ?? null,
                task?.LdmIsMandatory ?? false,
                task?.LdmIsVisible ?? false,
            )) {
                isValid = false;
            }
        }

        if (!fieldKey || fieldKey === 'Volume') {
            if (!validateField(
                'Volume',
                value ?? formData.Volume,
                task?.Volume ?? null,
                task?.VolumeIsMandatory ?? false,
                task?.VolumeIsVisible ?? false,
            )) {
                isValid = false;
            }
        }

        if (!fieldKey || fieldKey === 'Notes') {
            if (!validateField(
                'Notes',
                value ?? formData.Notes,
                null,
                task?.NotesIsMandatory ?? false,
                task?.NotesIsVisible ?? false,
            )) {
                isValid = false;
            }
        }

        return isValid;
    };

    if (isLoading) {
        return <Layout>
            <Header backUrl="#" title={i18n.t(String(task?.TaskCompletedButtonTranslationId))}></Header>
            <GlobalSpinner/>
        </Layout>;
    }

    return (
        <Layout>
            <Header
                backUrl={`/address/${trip?.TripKey}/${task?.TaskGuid}`}
                title={i18n.t(String(task?.TaskCompletedButtonTranslationId))}
            >
                <Button className="btn btn-secondary" type="submit" form="task-form">
                    <Icon className="text-primary" path={mdiCheck} size={.8}/>
                </Button>
            </Header>
            <div className="p-4">
                {task?.OrderNo !== '' && <h4 className="mb-3">
                    <span className="fw-bold">{i18n.t('590')}:</span> {task?.OrderNo}
                </h4>}

                {task?.CargoDescriptionHtml !== '' && <div>
                    <div dangerouslySetInnerHTML={{__html: task?.CargoDescriptionHtml ?? ''}}></div>
                </div>}

                {(task?.OrderNo !== '' || task?.CargoDescriptionHtml !== '') && <hr className="my-4"/>}

                <form onSubmit={handleSubmit} id="task-form">
                    <div>
                        {/* TaskCompletedDate */}
                        <div className="mb-3">
                            <p className="fw-bold mb-2">{i18n.t('2574')}</p>
                            <Datetime dateFormat="D.M.YYYY" timeFormat="HH:mm" value={formData.TaskCompletedDate} onChange={editDateField}/>
                        </div>

                        {/* PllCountIsVisible */}
                        {task?.PllCountIsVisible && <div>
                            <TaskActionField
                                fieldKey="PllCount"
                                value={formData.PllCount}
                                placeholder={task.PllCount}
                                feedback={validationMsg.PllCount}
                                onChange={editField}
                                TaskType={task.TaskType}
                                labelTranslationId={String(task.PllCountTranslationId)}
                                iconName={mdiShippingPallet}
                            />
                        </div>}

                        {/* PllStackableIsVisible */}
                        {task?.PllStackableIsVisible && <div>
                            <TaskActionField
                                fieldKey="PllStackable"
                                value={formData.PllStackable}
                                placeholder={task.PllStackable}
                                feedback={validationMsg.PllStackable}
                                onChange={editField}
                                TaskType={task.TaskType}
                                labelTranslationId={String(task.PllStackableTranslationId)}
                                iconName={mdiShippingPallet}
                            />
                        </div>}

                        {/* PllExchangeIsVisible */}
                        {task?.PllExchangeIsVisible && <div>
                            <TaskActionField
                                fieldKey="PllExchange"
                                value={formData.PllExchange}
                                placeholder={task.PllExchange}
                                feedback={validationMsg.PllExchange}
                                onChange={editField}
                                TaskType={task.TaskType}
                                labelTranslationId={String(task.PllExchangeTranslationId)}
                                iconName={mdiShippingPallet}
                            />
                        </div>}

                        {/* Ldm */}
                        {task?.LdmIsVisible && <div>
                            <TaskActionField
                                fieldKey="Ldm"
                                value={formData.Ldm}
                                placeholder={task.Ldm}
                                feedback={validationMsg.Ldm}
                                onChange={editField}
                                TaskType={task.TaskType}
                                labelTranslationId={String(task.LdmTranslationId)}
                                iconName={mdiShippingPallet}
                            />
                        </div>}

                        {/* Bruto */}
                        {task?.BrutoIsVisible && <div>
                            <TaskActionField
                                fieldKey="Bruto"
                                value={formData.Bruto}
                                placeholder={task.Bruto}
                                feedback={validationMsg.Bruto}
                                onChange={editField}
                                TaskType={task.TaskType}
                                labelTranslationId={String(task.BrutoTranslationId)}
                                iconName={mdiShippingPallet}
                            />
                        </div>}

                        {/* Volume */}
                        {task?.VolumeIsVisible && <div>
                            <TaskActionField
                                fieldKey="Volume"
                                value={formData.Volume}
                                placeholder={task.Volume}
                                feedback={validationMsg.Volume}
                                onChange={editField}
                                TaskType={task.TaskType}
                                labelTranslationId={String(task.VolumeTranslationId)}
                                iconName={mdiShippingPallet}
                            />
                        </div>}

                        {/* Temperature */}
                        {task?.TemperatureIsVisible && <div className="mb-3">
                            <p className="fw-bold mb-2">{i18n.t(String(task?.TemperatureTranslationId))}</p>
                            <div className="input-group">
                                <span className="input-group-text text-primary"><Icon path={mdiThermometer} size={.8}/></span>
                                <select
                                    className="form-control"
                                    name="Temperature"
                                    value={formData.Temperature ?? ''}
                                    onChange={editSelectField}
                                >
                                    <option value="" hidden>Select a value...</option>
                                    {general.temp.map((value) =>
                                        <option key={value} value={value}>{value}</option>)}
                                </select>
                            </div>
                            <FeedbackField feedback={validationMsg.Temperature}/>
                            </div>}

                        {/* Notes */}
                        {task?.NotesIsVisible && <div>
                            <TaskActionField
                                fieldKey="Notes"
                                value={formData.Notes}
                                placeholder={null}
                                feedback={validationMsg.Notes}
                                onChange={editField}
                                TaskType={task.TaskType}
                                labelTranslationId={String(task.NotesTranslationId)}
                                iconName={mdiMessageAlertOutline}
                            />
                        </div>}
                    </div>
                    <Button className="btn w-100 my-2" type="submit">{i18n.t('2639')}</Button>
                </form>
            </div>
        </Layout>
    );
}